import classNames from 'classnames';
import { type FC, type ReactNode, memo } from 'react';

import { IpfError } from '@ipfabric/frontend-shared-assets/utils/IpfError';
import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import {
  type PathLookupDecision,
  type PathLookupDecisionEvent,
  type Security,
  isNonNullable,
  isNullable,
} from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';

import { Link } from '@ipfabric/ui-components/Link/Link';
import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import type { DeviceViewEntityId } from 'components/DeviceView/deviceViewTypes';
import {
  type DeviceViewDispatch,
  useDeviceViewContextSelectors,
} from 'components/DeviceView/model/DeviceViewModel';
import { showTable } from 'components/DeviceView/model/commands/showTable';
import type { DecidingRuleScope } from 'components/SecuritySchema/model/SecuritySchemaEntryPoint';
import { SeverityIcon } from 'components/icons/SeverityIcon';
import { useLocale } from 'containers/App/LocaleContext';
import type { AppTranslations } from 'translations/translations';
import { isObject } from 'utils/object/isObject';
import { getIconSeverityShapeClassName } from './getIconSeverityShapeClassName';
import { getPathInspectorTableFilters } from './getPathInspectorTableFilters';
import { getPathInspectorTableSort } from './getPathInspectorTableSort';
import { getPathInspectorWebEndpoint } from './getPathInspectorWebEndpoint';
import {
  hasDecisionCellValue,
  hasEventFilterData,
} from './helpers/hasEventFilterData';
import { isSeverityEvent } from './helpers/isSeverityEvent';
import { pathInspectorTables } from './pathInspectorTables';

interface Props {
  deviceViewEntityId: DeviceViewEntityId;
  onShowSecurityCheckDetail: (scope: DecidingRuleScope) => void;
  trace: PathLookupDecision;
  traceIndex: number;
}

interface EventTdProps {
  children: ReactNode;
  last?: boolean;
}

const hasFollowingRows = (
  events: PathLookupDecisionEvent[],
  eventIndex: number,
) =>
  events.some(
    (_event, index) =>
      index > eventIndex && !isAggregatedEventLine(events, index),
  );

const canAggregateEvent = (event?: PathLookupDecisionEvent): boolean =>
  event?.type === 'remove header' ||
  event?.type === 'patch header' ||
  event?.type === 'insert header' ||
  event?.type === 'virtual routing' ||
  (event?.type === 'drop packet' && event?.reason !== 'Nexthop not found.');

const canAggregatePreviousEvent = (event?: PathLookupDecisionEvent): boolean =>
  event?.type === 'source NAT' ||
  event?.type === 'destination NAT' ||
  event?.type === 'policy based routing' ||
  canAggregateEvent(event);

const isPatchIpEvent = (event: PathLookupDecisionEvent) =>
  event.type === 'patch header' && event.headerType === 'ip';

const isConsecutiveEvent = (
  predicate: (event: PathLookupDecisionEvent) => boolean,
  eventIndex: number,
  events: PathLookupDecisionEvent[],
  // Fixed noUncheckedIndexedAccess NIM-19408
) => predicate(events[eventIndex]!) && predicate(events[eventIndex - 1]!);

const canAggregateRepeatedEventType = (
  events: PathLookupDecisionEvent[],
  eventIndex: number,
) => isConsecutiveEvent(isPatchIpEvent, eventIndex, events);

/**
 * Aggregate specified types of events that contain only 3rd column into previous line.
 *
 * If it's first line, don't show empty cell (show the event to aggregate).
 * If it's event to aggregate and has parent, omit 1 row
 * to move events to aggregate to start in paren't row.
 */
const isAggregatedEventLine = (
  events: PathLookupDecisionEvent[],
  eventIndex: number,
): boolean => {
  const event = events[eventIndex];
  return (
    canAggregateEvent(event) &&
    eventIndex !== 0 &&
    (!canAggregatePreviousEvent(events[eventIndex - 1]) ||
      canAggregateRepeatedEventType(events, eventIndex))
  );
};

const getPacketActionEvent = (
  events: PathLookupDecisionEvent[],
  eventIndex: number,
  event: PathLookupDecisionEvent,
) =>
  isAggregatedEventLine(events, eventIndex + 1)
    ? events[eventIndex + 1]
    : event;

const getNonSecurityDescription = (
  msg: AppTranslations,
  event: PathLookupDecisionEvent,
  showSecurityCheckDetail: () => void,
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: big switch
): ReactNode => {
  if (event.type === 'remove header') {
    if (event.index !== undefined && event.index > 0) {
      return `${msg.deviceView.devicePathLookup.decisionTable.remove}[${event.index}]`;
    }
    return msg.deviceView.devicePathLookup.decisionTable.pop;
  }
  if (event.type === 'insert header') {
    if (event.index !== undefined && event.index > 0) {
      return `${msg.deviceView.devicePathLookup.decisionTable.insert}[${event.index}]`;
    }
    return msg.deviceView.devicePathLookup.decisionTable.push;
  }
  if (event.type === 'patch header') {
    if (event.index !== undefined && event.index > 0) {
      return `${msg.deviceView.devicePathLookup.decisionTable.patch}[${event.index}]`;
    }
    return msg.deviceView.devicePathLookup.decisionTable.patch;
  }
  if (event.type === 'destination NAT' || event.type === 'source NAT') {
    if (event.field !== undefined) {
      return `${msg.deviceView.devicePathLookup.decisionTable.patch} ${event.field}`;
    }
    return msg.deviceView.devicePathLookup.decisionTable.notApplied;
  }
  if (event.type === 'policy based routing') {
    return event.field !== undefined
      ? `PBR: ${event.field}`
      : `PBR: ${msg.deviceView.devicePathLookup.decisionTable.notApplied}`;
  }
  if (event.type === 'drop packet') {
    if (event.reason !== undefined) {
      return `${msg.deviceView.devicePathLookup.decisionTable.drop}: ${event.reason} ${
        Array.isArray(event.ips) ? `(${event.ips.join(', ')})` : ''
      }`;
    }
    return event.type;
  }
  if (event.type === 'flood packet') {
    return `${msg.deviceView.devicePathLookup.decisionTable.flood}: ${event.reason}`;
  }
  if (event.type === 'accept packet') {
    if (event.reason !== undefined) {
      return `${msg.deviceView.devicePathLookup.decisionTable.accept}: ${event.reason}`;
    }
    return event.type;
  }
  if (
    event.type === 'security check' ||
    event.type === 'security check ignored'
  ) {
    return event.decidingRule && event.securityType ? (
      <Link
        onClick={() => {
          showSecurityCheckDetail();
        }}
        renderAs='button'
      >
        {event.type}
      </Link>
    ) : (
      msg.deviceView.devicePathLookup.decisionTable.ignoredSecurityCheck
    );
  }
  if (event.type === 'virtual routing') {
    return `${event.type}\n${msg.deviceView.devicePathLookup.decisionTable.interface}: ${event.ifaceName}`;
  }
  stumble(`Unknown event type ${event.type}`);
  return '';
};

const getSeverityDescription = (event: PathLookupDecisionEvent): ReactNode =>
  event.severityInfo?.details?.map((detail, index) => (
    // biome-ignore lint/suspicious/noArrayIndexKey: no better key
    <div key={index}>{detail}</div>
  ));

const getIconTooltipText = (
  event: PathLookupDecisionEvent,
  msg: AppTranslations,
): ReactNode => {
  if (isSeverityEvent(event)) {
    const { details } = event.severityInfo;
    return isNullable(details)
      ? (msg.deviceView.devicePathLookup.decisionTable.events.severityName[
          event.severityInfo.name
        ] ?? '')
      : undefined;
  }
  return event.type === 'table entry match'
    ? msg.deviceView.devicePathLookup.decisionTable.tableEntryFoundTooltip
    : msg.deviceView.devicePathLookup.decisionTable.tableEntryNotFoundTooltip;
};

const getPathInspectorSeverityIcon = (
  event: PathLookupDecisionEvent,
  msg: AppTranslations,
): ReactNode => (
  <Tooltip content={getIconTooltipText(event, msg)}>
    <SeverityIcon
      className='mr-lgu-2'
      faIcon={getIconSeverityShapeClassName(event)}
      severity={event.severityInfo!.severity}
    />
  </Tooltip>
);

const preventTextOverflow = (event: PathLookupDecisionEvent): boolean =>
  event.type === 'drop packet' ||
  event.type === 'accept packet' ||
  event.type === 'flood packet';

const breakTextLines = (event: PathLookupDecisionEvent): boolean =>
  event.type === 'virtual routing';

const getHumanReadableFilterValue = (
  value: any,
  msg: AppTranslations,
): string => {
  if (value === '') {
    return msg.deviceView.devicePathLookup.decisionTable.emptyFilterValue;
  }
  if (Array.isArray(value)) {
    return `[${value.map((item) => getHumanReadableFilterValue(item, msg)).join(', ')}]`;
  }
  if (isObject(value)) {
    return `{${getObjectFilterHumanReadableValue(msg, value)}}`;
  }
  return value;
};

const getObjectFilterHumanReadableValue = (msg: AppTranslations, value: any) =>
  Object.entries(value)
    .map(
      ([key, objectValue]: [key: string, objectValue: any]) =>
        `${key}: ${getHumanReadableFilterValue(objectValue, msg)}`,
    )
    .join(', ');

const renderPacketActionCellValueCommand = (
  msg: AppTranslations,
  event: PathLookupDecisionEvent,
  isAggregated: boolean,
  showSecurityCheckDetail: () => void,
): ReactNode => {
  // Severity has the highest priority to display detail.
  if (
    isSeverityEvent(event) &&
    event.type !== 'destination NAT' &&
    event.type !== 'source NAT' &&
    event.type !== 'policy based routing'
  ) {
    // An aggregated drop/accept/flood packet has no decision cell of its own, so
    // its "DROP: <reason>" would be lost if we rendered only the severity
    // `details`. Render the reason here instead.
    if (
      isAggregated &&
      (event.type === 'drop packet' ||
        event.type === 'accept packet' ||
        event.type === 'flood packet')
    ) {
      return getNonSecurityDescription(msg, event, showSecurityCheckDetail);
    }
    return <>{getSeverityDescription(event)}</>;
  }
  return getNonSecurityDescription(msg, event, showSecurityCheckDetail);
};

const getPacketActionCellValueFilters = (
  msg: AppTranslations,
  event: PathLookupDecisionEvent,
): string | undefined => {
  return `(${getObjectFilterHumanReadableValue(msg, event.header!)})`;
};

const renderPacketActionCellValue = (
  msg: AppTranslations,
  event: PathLookupDecisionEvent,
  isAggregated: boolean,
  showSecurityCheckDetail: () => void,
): ReactNode => {
  return (
    <span
      className={classNames(
        preventTextOverflow(event) && 'whitespace-nowrap',
        breakTextLines(event) && 'whitespace-pre',
      )}
    >
      {renderPacketActionCellValueCommand(
        msg,
        event,
        isAggregated,
        showSecurityCheckDetail,
      )}
      {` ${event.headerType ?? ''} ${
        event.header !== undefined
          ? getPacketActionCellValueFilters(msg, event)
          : ''
      }`}
    </span>
  );
};

const transformFiltersCellKey = (key: string) =>
  key === 'bdVnid' ? 'vni' : key;

const renderFiltersCellValue = (
  msg: AppTranslations,
  event: PathLookupDecisionEvent,
): ReactNode => {
  if (event.type === 'policy based routing') {
    const original = (event.value as Security.PbrIpOption)?.original;
    return original || '';
  }
  if (event.filter !== undefined) {
    return Object.entries(event.filter).map(([key, value]) => (
      <div key={key} className='whitespace-nowrap'>
        {transformFiltersCellKey(key)}
        {': '}
        {getHumanReadableFilterValue(value, msg)}
      </div>
    ));
  }
  if (event.field !== undefined && event.value !== undefined) {
    return `${event.field}: ${event.value}`;
  }
  return '';
};

const renderDecisionCellValue = (
  msg: AppTranslations,
  event: PathLookupDecisionEvent,
  dispatch: DeviceViewDispatch,
  deviceViewEntityId: DeviceViewEntityId,
  showSecurityCheckDetail: () => void,
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TBD eventually cleanup
): ReactNode => {
  const msgDecisionTable = msg.deviceView.devicePathLookup.decisionTable;
  if (event.type === 'packet data match') {
    return event.type;
  }
  if (event.type === 'remove header' && !!event.reason) {
    return event.reason;
  }
  if (event.type === 'route detail') {
    return `Route Detail: ${event.value}`;
  }
  if (
    event.type === 'table entry match' ||
    event.type === 'table entry not found'
  ) {
    const translatedType = event.table && pathInspectorTables[event.table];
    if (event.table === undefined || translatedType === undefined) {
      stumble(`Unknown event table ${event.table}`);
      return event.type;
    }

    const handleCellClick = () => {
      if (event.filter !== undefined && event.table !== undefined) {
        const webEndpoint = getPathInspectorWebEndpoint(event.table);
        if (webEndpoint === undefined) {
          return;
        }
        dispatch(showTable, {
          deviceViewEntityId,
          filter: getPathInspectorTableFilters(event),
          sort: getPathInspectorTableSort(event.table),
          webEndpoint,
        });
      }
    };

    const msgTableEntry =
      event.type === 'table entry match'
        ? msgDecisionTable.tableEntryMatch
        : msgDecisionTable.tableEntryNoMatch;
    const tableName = msgTableEntry.replace('{table}', translatedType);
    if (getPathInspectorWebEndpoint(event.table) === undefined) {
      return tableName;
    }

    const renderedDecisionLabel =
      event.type === 'table entry match' ? (
        <Link onClick={handleCellClick} renderAs='button'>
          {tableName}
        </Link>
      ) : (
        tableName
      );

    return (
      <>
        {isSeverityEvent(event) ? (
          getPathInspectorSeverityIcon(event, msg)
        ) : (
          <Tooltip
            content={
              event.type === 'table entry match'
                ? msgDecisionTable.tableEntryFoundTooltip
                : msgDecisionTable.tableEntryNotFoundTooltip
            }
          >
            <i
              className={classNames(
                'fa',
                'fa-fw',
                event.type === 'table entry match'
                  ? 'fa-check-circle'
                  : ['fa-exclamation-circle', 'text-red-600'],
                'mr-lgu-2',
              )}
            />
          </Tooltip>
        )}
        {renderedDecisionLabel}
      </>
    );
  }
  if (isSeverityEvent(event)) {
    return (
      <>
        {getPathInspectorSeverityIcon(event, msg)}
        {/* Multiple event types can display security detail
            https://ipfabric.atlassian.net/browse/NIM-5787 */}
        {event.type === 'severity event' ||
        event.type === 'security check' ||
        event.type === 'security check ignored' ||
        event.type === 'destination NAT' ||
        event.type === 'source NAT' ||
        event.type === 'policy based routing' ? (
          <>
            {(event.type === 'security check' ||
              event.type === 'security check ignored' ||
              event.type === 'destination NAT' ||
              event.type === 'source NAT' ||
              event.type === 'policy based routing') &&
            // Green and red 'security check' get deciding policy name, shorter name must be displayed.
            (event.severityInfo?.severity === 0 ||
              event.severityInfo?.severity === 30)
              ? msgDecisionTable.events.topicName[event.severityInfo.topic]
              : msgDecisionTable.events.severityName[event.severityInfo.name]}
            {isNonNullable(event.decidingPolicyName) && (
              <>
                {': '}
                <Link
                  onClick={() => {
                    showSecurityCheckDetail();
                  }}
                  renderAs='button'
                >
                  {event.decidingPolicyName}
                </Link>
                {' - '}
                {
                  msgDecisionTable[
                    event.severityInfo?.severity === 0 ||
                    event.severityInfo?.severity === 10
                      ? 'permit'
                      : 'deny'
                  ]
                }
                {event.type === 'security check ignored' && (
                  <>
                    {' ('}
                    {msgDecisionTable.notApplied})
                  </>
                )}
              </>
            )}
          </>
        ) : (
          getNonSecurityDescription(msg, event, showSecurityCheckDetail)
        )}
      </>
    );
  }
  stumble(`Unknown event type ${event.type}`);
  return '';
};

const EventTd: FC<EventTdProps> = (props) => (
  <td
    className={classNames(
      'pathlookup-tab-content-cell pathlookup-tab-content-cell--no-padding',
      props.last && 'pathlookup-tab-content-cell__last',
    )}
  >
    <div className='flex h-full flex-col'>{props.children}</div>
  </td>
);

const BarePathInspectorTableRow: FC<Props> = (props) => {
  const ctxProps = useDeviceViewContextSelectors({
    dispatch: prop('dispatch'),
  });
  const msg = useLocale();
  const { phase, events } = props.trace;
  const aggregatedEvents = events.filter(
    (_event, eventIndex) => !isAggregatedEventLine(events, eventIndex),
  );
  const aggregatedEventsLength = aggregatedEvents.length;

  const showSecurityCheckDetail = (event: PathLookupDecisionEvent) => {
    if (!event.decidingRule || !event.securityType) {
      throw new IpfError(
        'GENERIC',
        'Security check events must contain decidingRule and securityType',
      );
    }
    props.onShowSecurityCheckDetail({
      rulePath: event.decidingRule,
      type: event.securityType,
      // Keys the single-direction cloud machine; undefined for on-prem.
      cloudDirection: event.cloudDirection,
    });
  };
  return (
    <>
      {events.map((event: PathLookupDecisionEvent, eventIndex: number) =>
        isAggregatedEventLine(events, eventIndex) ? null : (
          <tr
            // biome-ignore lint/suspicious/noArrayIndexKey: no better key
            key={eventIndex}
            className={classNames(
              props.traceIndex % 2 === 1 && 'pathlookup-tab-content-row--odd',
              'h-full',
            )}
          >
            {eventIndex === 0 && (
              <td
                className='pathlookup-tab-content-cell__indent'
                rowSpan={aggregatedEventsLength}
              />
            )}
            {eventIndex === 0 && (
              <td
                className='pathlookup-tab-content-cell pathlookup-tab-content-cell__first whitespace-nowrap'
                rowSpan={aggregatedEventsLength}
              >
                {msg.deviceView.devicePathLookup.decisionTable.phase[phase]}
              </td>
            )}
            <EventTd>
              <div
                className={classNames(
                  hasFollowingRows(events, eventIndex) &&
                    'pathlookup-tab-content-event-cell--border',
                  'flex',
                  'flex-col',
                  'flex-1',
                  'justify-center',
                  'py-5',
                  'pr-5',
                  'ml-5',
                )}
              >
                <div className='whitespace-nowrap'>
                  {hasDecisionCellValue(event) &&
                    renderDecisionCellValue(
                      msg,
                      event,
                      ctxProps.dispatch,
                      props.deviceViewEntityId,
                      () => showSecurityCheckDetail(event),
                    )}
                </div>
              </div>
            </EventTd>
            <EventTd>
              <div
                className={classNames(
                  hasFollowingRows(events, eventIndex) &&
                    'pathlookup-tab-content-event-cell--border',
                  'flex',
                  'flex-col',
                  'flex-1',
                  'justify-center',
                  'p-5',
                )}
              >
                <div>
                  {hasEventFilterData(event) &&
                    renderFiltersCellValue(msg, event)}
                </div>
              </div>
            </EventTd>
            <EventTd last>
              <div
                className={classNames(
                  hasFollowingRows(events, eventIndex) &&
                    'pathlookup-tab-content-event-cell--border',
                  'flex',
                  'flex-col',
                  'flex-1',
                  'justify-center',
                  'py-5',
                  'pl-5',
                  'mr-5',
                )}
              >
                <div>
                  {!hasEventFilterData(
                    // Fixed noUncheckedIndexedAccess NIM-19408
                    getPacketActionEvent(events, eventIndex, event)!,
                  ) ||
                  isSeverityEvent(
                    // Fixed noUncheckedIndexedAccess NIM-19408
                    getPacketActionEvent(events, eventIndex, event)!,
                  ) ||
                  isAggregatedEventLine(events, eventIndex + 1) ||
                  event.type === 'destination NAT' ||
                  event.type === 'source NAT' ||
                  event.type === 'policy based routing'
                    ? renderPacketActionCellValue(
                        msg,
                        // Fixed noUncheckedIndexedAccess NIM-19408
                        getPacketActionEvent(events, eventIndex, event)!,
                        // The packet-action event is the next, aggregated line
                        // (e.g. a `drop packet`) only when this holds.
                        isAggregatedEventLine(events, eventIndex + 1),
                        () =>
                          showSecurityCheckDetail(
                            // Fixed noUncheckedIndexedAccess NIM-19408
                            getPacketActionEvent(events, eventIndex, event)!,
                          ),
                      )
                    : ''}
                </div>
              </div>
            </EventTd>
            {eventIndex === 0 && (
              <td
                className='pathlookup-tab-content-cell__indent'
                rowSpan={aggregatedEventsLength}
              />
            )}
          </tr>
        ),
      )}
    </>
  );
};

export const PathInspectorTableRow = memo(BarePathInspectorTableRow);
